/**
 * «Подбор» — ответ на запрос группы по фото: страница под телефон, **без входа**, по хэшу.
 * Вид — минимализм лендинга под Farfetch (`web/public/assets/design/landing/fable.html`): монохром,
 * радиусы 0, волосяные линии, Tenor Sans + Golos Text, плитка 2:3; фильтр — город, размер и «Все фильтры»
 * (свойства «Резерва», как каталог «Чатов»). Сверху — новый поиск: фото с телефона и/или номер серии.
 * Есть серия — «Эта модель», затем вся склейка (`modelSimilarGroupID`, все цвета), ниже похожие по фото.
 * Удобство — по UX-ревью `docs/specs/2026-10-28-podbor-ux-design.md`: цели касания от 44 px, фильтры
 * закреплены, фильтр строгий, «42 ↗» — серия в CRM РФ, «42 · 2 ▾» — выбрать экземпляр, «в пути» виден сразу.
 * Спека `docs/specs/2026-10-28-podbor-photo-design.md`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { ApiFailure } from "@/lib/api";
import type { CatalogGroup, CatalogValue } from "@/lib/clientChats";
import {
  BR,
  fits,
  loadPodbor,
  loadPodborFilters,
  matchPodbor,
  photoBase64,
  type PodborModel,
  type PodborSerial,
  rememberPodbor,
  searchPodbor,
} from "@/lib/podborAnswer";

const FONTS =
  "https://fonts.googleapis.com/css2?family=Golos+Text:wght@400;500;600&family=Tenor+Sans&display=swap";
/** Карточка серии в CRM РФ — там сотрудник видит место, брони и фото. */
const CRM_SERIAL = "https://crm-rf.unirenter.ru/#/info?serial=";
const PAGE = 12; // похожих за раз
const CITIES_IN_TILE = 2; // городов в карточке до «Ещё городов»

const CSS = `
.pod{--paper:#fff;--ink:#141414;--ink-2:#6b6661;--line:#e8e4de;--tint:#f5f2ee;
  --ui:"Golos Text",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  --display:"Tenor Sans","Golos Text",system-ui,sans-serif;
  --gx:max(16px,env(safe-area-inset-left,0px));
  min-height:100vh;background:var(--paper);color:var(--ink);font:400 14px/1.5 var(--ui);
  font-feature-settings:"tnum";-webkit-font-smoothing:antialiased}
.pod *{box-sizing:border-box}
.pod :where(button){font:inherit;color:inherit;background:none;border:0;padding:0;cursor:pointer;border-radius:0;
  -webkit-tap-highlight-color:transparent;touch-action:manipulation}
.pod :where(a){color:inherit;-webkit-tap-highlight-color:transparent}
.pod :focus-visible{outline:2px solid var(--ink);outline-offset:2px}
.pod :where(img){display:block;max-width:100%}
.pod :where(h1,h2,p){margin:0}
.pod-hdr{height:52px;display:flex;align-items:center;padding:0 var(--gx);border-bottom:1px solid var(--line)}
.pod-brand{font-family:var(--display);display:flex;align-items:baseline;gap:8px;margin-right:auto}
.pod-brand b{font-weight:400;font-size:24px;line-height:1;letter-spacing:-.03em}
.pod-brand span{font-size:11px;letter-spacing:.24em}
.pod-cap{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--ink-2)}
.pod-home{display:inline-flex;align-items:center;min-height:44px;text-decoration:none}
.pod-intro{display:grid;grid-template-columns:minmax(0,1fr) 76px;gap:16px;padding:24px var(--gx) 20px}
.pod-intro h1{font-family:var(--display);font-weight:400;font-size:28px;line-height:1.1;margin:6px 0 10px}
.pod-req{font-size:14px}
.pod-mute{color:var(--ink-2);font-size:13px}
.pod-link{display:inline-flex;align-items:center;min-height:44px;font-size:14px;font-weight:500;
  text-decoration:underline;text-underline-offset:4px;text-decoration-thickness:1px}
.pod-q{width:76px;aspect-ratio:2/3;object-fit:cover;background:var(--tint)}
.pod-filters{position:sticky;top:0;z-index:20;background:var(--paper);border-top:1px solid var(--line);
  border-bottom:1px solid var(--line);padding:6px 0}
.pod-row{display:grid;grid-template-columns:72px minmax(0,1fr);align-items:center}
.pod-row .pod-cap{padding-left:var(--gx)}
.pod-track{display:flex;gap:6px;overflow-x:auto;padding:4px var(--gx) 4px 0;scrollbar-width:none;
  -webkit-mask-image:linear-gradient(90deg,#000 85%,transparent);mask-image:linear-gradient(90deg,#000 85%,transparent)}
.pod-track::-webkit-scrollbar{display:none}
.pod-chip{flex:0 0 auto;min-width:44px;height:44px;padding:0 14px;border:1px solid var(--line)!important;
  display:inline-flex;align-items:center;justify-content:center;gap:6px;font-size:14px;white-space:nowrap}
.pod-chip .n{font-size:12px;color:var(--ink-2)}
.pod-chip[aria-pressed="true"]{background:var(--ink)!important;color:var(--paper);border-color:var(--ink)!important}
.pod-chip[aria-pressed="true"] .n{color:inherit;opacity:.65}
.pod-ctx{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px var(--gx) 0}
.pod-ctx .pod-link{font-weight:400;color:var(--ink-2)}
.pod-legend{padding:0 var(--gx);color:var(--ink-2);font-size:12px}
.pod-sec{display:flex;align-items:baseline;justify-content:space-between;gap:12px;padding:32px var(--gx) 14px}
.pod-sec h2{font-family:var(--display);font-weight:400;font-size:22px;line-height:1.15}
.pod-wide{display:grid;grid-template-columns:42% minmax(0,1fr);gap:16px;padding:0 var(--gx) 24px}
.pod-media{position:relative;aspect-ratio:2/3;background:var(--tint);overflow:hidden}
.pod-media img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.pod-media .none{position:absolute;inset:0;display:grid;place-items:center;color:var(--ink-2);font-size:12px}
.pod-model{font-size:15px;font-weight:500;line-height:1.3}
.pod-price{font-size:14px}
.pod-av{margin-top:14px;display:grid;gap:14px}
.pod-city-line{display:flex;flex-wrap:wrap;gap:6px;align-items:center}
.pod-city{height:44px;min-width:52px;padding:0 10px;background:var(--tint)!important;font-size:12px;font-weight:600;
  letter-spacing:.1em}
.pod-sz{min-width:52px;height:44px;padding:0 10px;border:1px solid var(--ink)!important;display:inline-flex;
  align-items:center;justify-content:center;gap:4px;font-size:14px;font-weight:500;text-decoration:none}
.pod-sz .s{font-size:12px;font-weight:400}
.pod-sz[aria-expanded="true"]{background:var(--ink)!important;color:var(--paper)}
.pod-sz.me{box-shadow:inset 0 -3px 0 var(--ink)}
.pod-sz.me[aria-expanded="true"]{box-shadow:inset 0 -3px 0 var(--paper)}
.pod-note{margin-top:6px;font-size:13px;color:var(--ink-2)}
.pod-serials{margin-top:8px;border-top:1px solid var(--line)}
.pod-serial{display:flex;align-items:center;justify-content:space-between;gap:8px;min-height:44px;
  border-bottom:1px solid var(--line);font-size:14px;text-decoration:none}
.pod-serial .r{color:var(--ink-2);font-size:13px}
.pod-text{min-height:44px;font-size:13px;color:var(--ink-2)!important;text-decoration:underline;text-underline-offset:4px;
  text-align:left}
.pod-empty{display:grid;gap:4px;margin-top:12px}
.pod-empty .acts{display:flex;flex-wrap:wrap;column-gap:16px}
.pod-related{margin:0 var(--gx);border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.pod-related>summary{min-height:52px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;
  list-style:none;font-size:14px}
.pod-related>summary::-webkit-details-marker{display:none}
.pod-related>summary::after{content:"+";font-size:20px;font-weight:300}
.pod-related[open]>summary::after{content:"−"}
.pod-related .pod-wide{padding:8px 0 24px}
.pod-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:32px 12px;padding:0 var(--gx)}
.pod-tile .body{padding-top:10px}
.pod-tile .pod-av{gap:12px}
.pod-tile .pod-city-line{gap:5px}
.pod-tile .pod-sz{min-width:44px;padding:0 8px}
.pod-tile .pod-city{min-width:44px;padding:0 8px}
.pod-pager{display:grid;gap:10px;padding:32px var(--gx) 8px;justify-items:center}
.pod-more{width:100%;height:48px;border:1px solid var(--ink)!important;font-size:14px;font-weight:500}
.pod-foot{margin-top:24px;padding:20px var(--gx) max(32px,env(safe-area-inset-bottom,0px));
  border-top:1px solid var(--line);color:var(--ink-2);font-size:12px}
.pod-state{padding:48px var(--gx);display:grid;gap:12px;justify-items:start}
.pod-state h1{font-family:var(--display);font-weight:400;font-size:26px}
.pod-skel{background:var(--tint)}
.pod-find{display:flex;gap:6px;padding:12px var(--gx);border-bottom:1px solid var(--line)}
.pod-input{flex:1;min-width:0;height:44px;border:1px solid var(--line);border-radius:0;padding:0 12px;
  background:var(--paper);color:var(--ink);font:400 16px/1 var(--ui)}
.pod-input:focus{outline:none;border-color:var(--ink)}
.pod-btn{flex:0 0 auto;height:44px;padding:0 14px;border:1px solid var(--ink)!important;display:inline-flex;
  align-items:center;justify-content:center;font-size:14px;font-weight:500;cursor:pointer}
.pod-btn.dark{background:var(--ink)!important;color:var(--paper)}
.pod-btn:disabled{opacity:.5;cursor:default}
.pod-thumb{flex:0 0 auto;width:44px;height:44px;position:relative}
.pod-thumb img{width:44px;height:44px;object-fit:cover}
.pod-thumb span{position:absolute;right:0;top:0;background:var(--ink);color:var(--paper);font-size:11px;
  line-height:14px;width:14px;text-align:center}
.pod-err{padding:0 var(--gx) 10px;color:#b42318;font-size:13px}
.pod-sw{width:12px;height:12px;border-radius:50%;border:1px solid var(--line);flex:0 0 auto}
.pod-sheet{position:fixed;inset:0;z-index:50;background:var(--paper);display:flex;flex-direction:column}
.pod-sheet-hd{flex:0 0 52px;display:flex;align-items:center;justify-content:space-between;padding:0 var(--gx);
  border-bottom:1px solid var(--line)}
.pod-sheet-hd h2{font-family:var(--display);font-weight:400;font-size:22px}
.pod-x{width:44px;height:44px;font-size:22px;font-weight:300;margin-right:-12px}
.pod-sheet-body{flex:1;overflow-y:auto;padding:0 var(--gx)}
.pod-grp{border-bottom:1px solid var(--line)}
.pod-grp-hd{width:100%;min-height:52px;display:flex;align-items:center;justify-content:space-between;gap:12px;
  font-size:15px;text-align:left}
.pod-grp-hd .n{font-size:12px;color:var(--ink-2)}
.pod-vals{display:flex;flex-wrap:wrap;gap:6px;padding:0 0 16px}
.pod-sheet-ft{flex:0 0 auto;display:grid;grid-template-columns:auto minmax(0,1fr);gap:12px;align-items:center;
  padding:10px var(--gx) max(10px,env(safe-area-inset-bottom,0px));border-top:1px solid var(--line)}
.pod-go{height:48px;background:var(--ink)!important;color:var(--paper);font-size:14px;font-weight:500}
@media(min-width:900px){.pod-main{max-width:1180px;margin:0 auto}.pod-grid{grid-template-columns:repeat(4,minmax(0,1fr))}
  .pod-wide{grid-template-columns:280px minmax(0,1fr)}.pod-intro{grid-template-columns:minmax(0,1fr) 110px}
  .pod-q{width:110px}}
@media(prefers-reduced-motion:reduce){.pod *{scroll-behavior:auto!important;transition:none!important}}
`;

function rub(n: number | null): string {
  return n ? `прокат от ${n.toLocaleString("ru-RU")} ₽` : "цена — в CRM";
}

function sizeOrder(v: string): number {
  if (v === BR) return 1e6;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : 1e5;
}

function bySize(a: string, b: string): number {
  return sizeOrder(a) - sizeOrder(b);
}

function Photo({ m }: { m: PodborModel }) {
  return (
    <div className="pod-media">
      {m.photo ? (
        <img src={m.photo} alt={`Модель ${m.model_id}`} loading="lazy" />
      ) : (
        <span className="none">Фото нет</span>
      )}
    </div>
  );
}

type Group = { id: number; city: string; sizes: [string, PodborSerial[]][] };

/** Серии → города по порядку `order` (город серии запроса — первым), внутри — размеры по возрастанию. */
function groups(serials: PodborSerial[], order: number[]): Group[] {
  const map = new Map<
    number,
    { id: number; city: string; bySize: Map<string, PodborSerial[]> }
  >();
  for (const s of serials) {
    const g = map.get(s.location_id) ?? {
      id: s.location_id,
      city: s.city,
      bySize: new Map(),
    };
    g.bySize.set(s.size, [...(g.bySize.get(s.size) ?? []), s]);
    map.set(s.location_id, g);
  }
  const place = (id: number) => {
    const i = order.indexOf(id);
    return i < 0 ? 1e6 : i;
  };
  return [...map.values()]
    .sort((a, b) => place(a.id) - place(b.id))
    .map((g) => ({
      id: g.id,
      city: g.city,
      sizes: [...g.bySize.entries()].sort((x, y) => bySize(x[0], y[0])),
    }));
}

/** Наличие модели: строка на город — город (фильтр) и размеры. «42 ↗» — одна серия, сразу CRM;
 * «42 · 2 ▾» — несколько, раскрывает экземпляры. «В пути» — строкой под городом, до перехода. */
function Availability({
  serials,
  order,
  mark,
  limit,
  onCity,
}: {
  serials: PodborSerial[];
  order: number[];
  mark?: number | undefined;
  limit?: number;
  onCity: (id: number) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const list = groups(serials, order);
  const shown = limit && !all ? list.slice(0, limit) : list;
  return (
    <div className="pod-av">
      {shown.map((g) => {
        const opened = g.sizes.find(([v]) => open === `${g.id}|${v}`);
        const transit = g.sizes
          .flatMap(([, ss]) => ss)
          .filter((s) => s.transit);
        return (
          <div key={g.id}>
            <div className="pod-city-line">
              <button
                type="button"
                className="pod-city"
                onClick={() => onCity(g.id)}
                aria-label={`Показать только ${g.city}`}
              >
                {g.city}
              </button>
              {g.sizes.map(([v, ss]) => {
                const key = `${g.id}|${v}`;
                const me = ss.some((s) => s.serial === mark);
                const one = ss.length === 1 ? ss[0] : undefined;
                return one ? (
                  <a
                    key={v}
                    className={`pod-sz${me ? " me" : ""}`}
                    href={CRM_SERIAL + one.serial}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${g.city}, размер ${v}, серия ${one.serial}${one.transit ? ", в пути" : ""} — открыть в CRM`}
                  >
                    {v}
                    <span className="s">↗</span>
                  </a>
                ) : (
                  <button
                    key={v}
                    type="button"
                    className={`pod-sz${me ? " me" : ""}`}
                    aria-expanded={open === key}
                    aria-label={`${g.city}, размер ${v}: экземпляров ${ss.length}`}
                    onClick={() => setOpen(open === key ? null : key)}
                  >
                    {v}
                    <span className="s">· {ss.length} ▾</span>
                  </button>
                );
              })}
            </div>
            {transit.length ? (
              <div className="pod-note">
                в пути в {g.city}:{" "}
                {[...new Set(transit.map((s) => s.size))]
                  .sort(bySize)
                  .join(", ")}
              </div>
            ) : null}
            {opened ? (
              <div className="pod-serials">
                {opened[1].map((s) => (
                  <a
                    key={s.serial}
                    className="pod-serial"
                    href={CRM_SERIAL + s.serial}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Серия {s.serial} ↗</span>
                    <span className="r">
                      {[
                        s.serial === mark ? "из запроса" : "",
                        s.transit ? "в пути" : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {limit && list.length > limit ? (
        <button
          type="button"
          className="pod-text"
          onClick={() => setAll((v) => !v)}
        >
          {all ? "Свернуть города" : `Ещё городов: ${list.length - limit}`}
        </button>
      ) : null}
    </div>
  );
}

type Filter = {
  city: number | null;
  size: string;
  /** Серии под «Все фильтры»; null — свойства не выбраны. */
  match: Set<number> | null;
  order: number[];
  mark?: number | undefined;
  onCity: (id: number) => void;
  widen: (what: "city" | "size") => void;
};

/** Пусто под фильтр — честно говорим и даём расширить по одному условию. */
function Nothing({ f }: { f: Filter }) {
  return (
    <div className="pod-empty">
      <div className="pod-mute">
        Под выбранные фильтры экземпляров нет.
      </div>
      <div className="acts">
        {f.city !== null ? (
          <button
            type="button"
            className="pod-text"
            onClick={() => f.widen("city")}
          >
            Все города
          </button>
        ) : null}
        {f.size ? (
          <button
            type="button"
            className="pod-text"
            onClick={() => f.widen("size")}
          >
            Любой размер
          </button>
        ) : null}
      </div>
    </div>
  );
}

function WideModel({ m, sub, f }: { m: PodborModel; sub: string; f: Filter }) {
  const list = m.serials.filter((s) => fits(s, f.city, f.size, f.match));
  return (
    <div className="pod-wide">
      <Photo m={m} />
      <div>
        <div className="pod-model">Модель {m.model_id}</div>
        <div className="pod-price">{rub(m.price)}</div>
        <div className="pod-mute">
          {m.name} · {sub}
        </div>
        {list.length ? (
          <Availability
            serials={list}
            order={f.order}
            mark={f.mark}
            onCity={f.onCity}
          />
        ) : (
          <Nothing f={f} />
        )}
      </div>
    </div>
  );
}

function Tile({ m, f, sub }: { m: PodborModel; f: Filter; sub?: string }) {
  const list = m.serials.filter((s) => fits(s, f.city, f.size, f.match));
  return (
    <div className="pod-tile">
      <Photo m={m} />
      <div className="body">
        <div className="pod-model">Модель {m.model_id}</div>
        <div className="pod-price">{rub(m.price)}</div>
        <div className="pod-mute">{sub ? `${m.name} · ${sub}` : m.name}</div>
        <Availability
          serials={list}
          order={f.order}
          mark={f.mark}
          limit={CITIES_IN_TILE}
          onCity={f.onCity}
        />
      </div>
    </div>
  );
}

/** Запуск поиска — общий у главной `/podbor` и строки поиска на странице ответа: фото и/или серия →
 * новая страница ответа; поиск запоминается в «Недавних» этого телефона. */
export function useStartSearch() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const start = async (
    file: File | null,
    serial: string,
    filter: { city: number | null; size: string } = { city: null, size: "" },
  ): Promise<boolean> => {
    const n = serial.replace(/\D/g, "");
    if (!file && !n) {
      setError("Загрузите фото или введите номер серии");
      return false;
    }
    setBusy(true);
    setError("");
    try {
      const photo = file ? await photoBase64(file) : "";
      const r = await searchPodbor({ photo, serial: n ? Number(n) : null, size: filter.size, location_id: filter.city });
      rememberPodbor({ token: r.token, serial: n ? Number(n) : null, photo: Boolean(file), at: new Date().toISOString() });
      navigate(`/podbor/${r.token}`);
      window.scrollTo({ top: 0 });
      return true;
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : "Не удалось прочитать фото — попробуйте другое");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { start, busy, error, setError };
}

/** Превью выбранного фото — адрес объекта, который освобождается при смене файла. */
export function usePreview(file: File | null): string | null {
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return preview;
}

/** Строка нового поиска на странице ответа. */
function NewSearch({ city, size }: { city: number | null; size: string }) {
  const [serial, setSerial] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const preview = usePreview(file);
  const { start, busy, error } = useStartSearch();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await start(file, serial, { city, size })) {
      setSerial("");
      setFile(null);
    }
  };

  return (
    <>
      <form className="pod-find" onSubmit={submit}>
        <input
          className="pod-input"
          inputMode="numeric"
          placeholder="Номер серии"
          aria-label="Номер серии"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
        />
        {preview ? (
          <button type="button" className="pod-thumb" aria-label="Убрать фото" onClick={() => setFile(null)}>
            <img src={preview} alt="" />
            <span>×</span>
          </button>
        ) : (
          <label className="pod-btn">
            Фото
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </label>
        )}
        <button type="submit" className="pod-btn dark" disabled={busy}>
          {busy ? "Ищем…" : "Найти"}
        </button>
      </form>
      {error ? <div className="pod-err">{error}</div> : null}
    </>
  );
}

/** Значение фильтра выбрано, если выбраны все его id (одноимённые свойства склеены через запятую). */
function picked(v: CatalogValue, props: Set<string>): boolean {
  return v.id.split(",").every((id) => props.has(id));
}

/** «Все фильтры» — свойства «Резерва» аккордеоном, как каталог «Чатов»: свёрнутые группы и спрятанные
 * значения — как там. Выбор применяется сразу; кнопка внизу — сколько моделей осталось. */
function AllFilters({
  groups,
  props,
  count,
  loading,
  onToggle,
  onReset,
  onClose,
}: {
  groups: CatalogGroup[];
  props: Set<string>;
  count: number;
  loading: boolean;
  onToggle: (v: CatalogValue) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [all, setAll] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const was = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = was;
    };
  }, [onClose]);

  return (
    <div className="pod-sheet" role="dialog" aria-modal="true" aria-label="Все фильтры">
      <div className="pod-sheet-hd">
        <h2>Фильтры</h2>
        <button type="button" className="pod-x" aria-label="Закрыть" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="pod-sheet-body">
        {loading ? <p className="pod-mute" style={{ padding: "16px 0" }}>Загружаем фильтры…</p> : null}
        {groups.map((g) => {
          const on = g.values.filter((v) => picked(v, props)).length;
          const isOpen = open[g.key] ?? (!g.collapsed || on > 0);
          const hidden = g.values.filter((v) => v.hide && !picked(v, props)).length;
          const values = all[g.key] ? g.values : g.values.filter((v) => !v.hide || picked(v, props));
          return (
            <div className="pod-grp" key={g.key}>
              <button
                type="button"
                className="pod-grp-hd"
                aria-expanded={isOpen}
                onClick={() => setOpen((had) => ({ ...had, [g.key]: !isOpen }))}
              >
                <span>
                  {g.name} {on ? <span className="n">· {on}</span> : null}
                </span>
                <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen ? (
                <div className="pod-vals">
                  {values.map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      className="pod-chip"
                      aria-pressed={picked(v, props)}
                      onClick={() => onToggle(v)}
                    >
                      {v.color ? <span className="pod-sw" style={{ background: v.color }} /> : null}
                      {v.name}
                    </button>
                  ))}
                  {hidden && !all[g.key] ? (
                    <button
                      type="button"
                      className="pod-text"
                      onClick={() => setAll((had) => ({ ...had, [g.key]: true }))}
                    >
                      Все · ещё {hidden}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="pod-sheet-ft">
        <button type="button" className="pod-link" onClick={onReset}>
          Сбросить
        </button>
        <button type="button" className="pod-go" onClick={onClose}>
          Показать моделей: {count}
        </button>
      </div>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONTS;
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
  return (
    <div className="pod">
      <style>{CSS}</style>
      <header className="pod-hdr">
        <div className="pod-brand">
          <b>UR</b>
          <span>UNIRENTER</span>
        </div>
        <Link className="pod-cap pod-home" to="/podbor">
          Подбор
        </Link>
      </header>
      {children}
    </div>
  );
}

export function PodborAnswerPage() {
  const { token = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const [shown, setShown] = useState(PAGE);
  const [sheet, setSheet] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const q = useQuery({
    queryKey: ["podbor", token],
    queryFn: () => loadPodbor(token),
    retry: false,
  });

  useEffect(() => {
    document.title = "Подбор · UNIRENTER";
  }, []);

  const data = q.data;
  const models = useMemo(
    () => [...(data?.same ?? []), ...(data?.similar ?? [])],
    [data],
  );
  const allSerials = useMemo(() => models.flatMap((m) => m.serials), [models]);
  const requested = useMemo(
    () => allSerials.find((s) => s.serial === data?.serial?.serial),
    [allSerials, data],
  );

  // «Все фильтры»: id свойств в адресе (`props=12,15`) — ссылкой можно поделиться с выбором.
  const propsParam = params.get("props") ?? "";
  const props = useMemo(
    () => new Set(propsParam.split(",").filter(Boolean)),
    [propsParam],
  );
  const filtersQ = useQuery({
    queryKey: ["podbor-filters", token],
    queryFn: () => loadPodborFilters(token),
    enabled: sheet || props.size > 0,
    staleTime: 10 * 60_000,
  });
  const groups = filtersQ.data?.groups ?? [];
  const matchQ = useQuery({
    queryKey: ["podbor-match", token, propsParam],
    queryFn: () =>
      matchPodbor(
        token,
        models.map((m) => m.model_id),
        [...props],
      ),
    enabled: props.size > 0 && models.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const match = useMemo(
    () => (props.size && matchQ.data ? new Set(matchQ.data.serials) : null),
    [props, matchQ.data],
  );

  const cityParam = params.get("city");
  const sizeParam = params.get("size");
  const present = new Set(allSerials.map((s) => s.location_id));
  const city =
    cityParam === "all"
      ? null
      : cityParam
        ? Number(cityParam)
        : data?.location_id && present.has(data.location_id)
          ? data.location_id
          : null;
  const size = sizeParam === "all" ? "" : (sizeParam ?? data?.size ?? "");
  const filterKey = `${city}|${size}|${propsParam}|${match ? match.size : "-"}`;

  useEffect(() => setShown(PAGE), [filterKey]);
  // Выбранный чип — в видимую часть ряда, без перестановки порядка.
  useEffect(() => {
    for (const track of filtersRef.current?.querySelectorAll<HTMLElement>(
      ".pod-track",
    ) ?? []) {
      const on = track.querySelector<HTMLElement>('[aria-pressed="true"]');
      if (on)
        track.scrollLeft = Math.max(0, on.offsetLeft - track.offsetLeft - 72);
    }
  }, [filterKey, data]);

  const set = (patch: Partial<Record<"city" | "size" | "props", string>>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const toggle = (v: CatalogValue) => {
    const next = new Set(props);
    const ids = v.id.split(",");
    if (picked(v, props)) for (const id of ids) next.delete(id);
    else for (const id of ids) next.add(id);
    set({ props: [...next].join(",") });
  };
  const toFilters = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (q.isLoading) {
    return (
      <Shell>
        <div className="pod-state" aria-busy="true">
          <div className="pod-mute">Загружаем подбор…</div>
          <div className="pod-skel" style={{ width: "60%", height: 28 }} />
          <div className="pod-skel" style={{ width: "100%", height: 44 }} />
          <div
            className="pod-skel"
            style={{ width: "42%", aspectRatio: "2/3" }}
          />
        </div>
      </Shell>
    );
  }
  if (!data) {
    const missing = q.error instanceof ApiFailure && q.error.status === 404;
    return (
      <Shell>
        <div className="pod-state">
          <h1>
            {missing ? "Подбор не найден" : "Не удалось загрузить подбор"}
          </h1>
          <p className="pod-mute">
            {missing
              ? "По этой ссылке ответа нет. Проверьте, что ссылка скопирована целиком, или попросите новую в группе «Подбор»."
              : "Проверьте соединение и попробуйте ещё раз."}
          </p>
          {missing ? null : (
            <button
              type="button"
              className="pod-more"
              onClick={() => q.refetch()}
            >
              Повторить
            </button>
          )}
        </div>
      </Shell>
    );
  }

  // Порядок городов — как в CRM; город серии запроса — первым.
  const order = [
    ...(data.serial?.location_id ? [data.serial.location_id] : []),
    ...data.locations.map((l) => l.id),
  ];
  const f: Filter = {
    city,
    size,
    match,
    order,
    mark: data.serial?.serial,
    onCity: (id) => set({ city: String(id) }),
    widen: (what) => set({ [what]: "all" }),
  };
  const passes = (m: PodborModel) =>
    m.serials.some((s) => fits(s, city, size, match));
  const sizes = [...new Set(allSerials.map((s) => s.size))]
    .filter((v) => v !== BR)
    .sort(bySize);
  // У города — сколько моделей есть в нём под выбранные размер и свойства.
  const cities = data.locations
    .filter((l) => present.has(l.id))
    .map((l) => ({
      ...l,
      n: models.filter((m) =>
        m.serials.some(
          (s) => s.location_id === l.id && fits(s, null, size, match),
        ),
      ).length,
    }));
  const self = data.same.filter((m) => m.link === "self");
  // Склейка (`modelSimilarGroupID`) и «Одинаковые» CRM — то же платье в других цветах.
  const glued = data.same.filter((m) => m.link !== "self");
  const gluedShown = glued.filter(passes);
  const similar = data.similar.filter(passes);
  const total = models.filter(passes).length;
  // Выбранные значения «Всех фильтров» — чипами с крестиком в ряду фильтров.
  const active = groups.flatMap((g) =>
    g.values.filter((v) => picked(v, props)),
  );
  const cityName = data.locations.find((l) => l.id === city)?.short;
  const serialCity = data.locations.find(
    (l) => l.id === data.serial?.location_id,
  )?.short;
  // `date_create` ставят часы базы — UTC, без пояса в строке.
  const when = new Date(`${data.date}Z`).toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Shell>
      <main className="pod-main">
        <NewSearch city={city} size={size} />
        <section className="pod-intro">
          <div>
            <div className="pod-cap">
              {data.serial ? "Серия из запроса" : "Подбор по фото"}
            </div>
            <h1>
              {data.serial ? `Серия ${data.serial.serial}` : "Похожие наряды"}
            </h1>
            {data.serial ? (
              <>
                <div className="pod-req">
                  {requested
                    ? [
                        requested.city,
                        requested.size,
                        requested.transit ? "в пути" : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : `Среди свободных нет${serialCity ? ` · учёт: ${serialCity}` : ""} — проверьте в CRM`}
                </div>
                <a
                  className="pod-link"
                  href={CRM_SERIAL + data.serial.serial}
                  target="_blank"
                  rel="noreferrer"
                >
                  Открыть серию в CRM ↗
                </a>
              </>
            ) : (
              <div className="pod-req">
                Точная модель не определена — ищем похожие по фото.
              </div>
            )}
            <div className="pod-mute">
              {data.note ? `${data.note} · ` : ""}запрос от {when}
            </div>
          </div>
          {data.has_photo ? (
            <img
              className="pod-q"
              src={`/api/podbor/${encodeURIComponent(data.token)}/photo`}
              alt="Фото запроса"
            />
          ) : null}
        </section>

        <div className="pod-filters" ref={filtersRef}>
          <div className="pod-row">
            <span className="pod-cap">Город</span>
            <div className="pod-track">
              <button
                type="button"
                className="pod-chip"
                aria-pressed={city === null}
                onClick={() => set({ city: "all" })}
              >
                Все
              </button>
              {cities.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className="pod-chip"
                  aria-pressed={city === c.id}
                  aria-label={`${c.short}: моделей ${c.n}`}
                  onClick={() => set({ city: String(c.id) })}
                >
                  {c.short} <span className="n">{c.n}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="pod-row">
            <span className="pod-cap">Размер</span>
            <div className="pod-track">
              <button
                type="button"
                className="pod-chip"
                aria-pressed={!size}
                onClick={() => set({ size: "all" })}
              >
                Все
              </button>
              {sizes.map((s) => (
                <button
                  type="button"
                  key={s}
                  className="pod-chip"
                  aria-pressed={size === s}
                  onClick={() => set({ size: s })}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="pod-row">
            <span className="pod-cap">Ещё</span>
            <div className="pod-track">
              <button
                type="button"
                className="pod-chip"
                aria-pressed={props.size > 0}
                aria-haspopup="dialog"
                onClick={() => setSheet(true)}
              >
                Все фильтры
                {props.size ? <span className="n">{active.length || ""}</span> : null}
              </button>
              {active.map((v) => (
                <button
                  type="button"
                  key={v.id}
                  className="pod-chip"
                  aria-label={`Снять фильтр ${v.name}`}
                  onClick={() => toggle(v)}
                >
                  {v.color ? (
                    <span className="pod-sw" style={{ background: v.color }} />
                  ) : null}
                  {v.name} <span className="n">×</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pod-ctx">
          <span>
            {[
              cityName ?? "Все города",
              size ? `размер ${size}` : "все размеры",
              props.size ? `фильтров: ${active.length || props.size}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
            {props.size && matchQ.isFetching ? " · считаем…" : ""}
          </span>
          {city !== null || size || props.size ? (
            <button
              type="button"
              className="pod-link"
              onClick={() => set({ city: "all", size: "all", props: "" })}
            >
              Сбросить
            </button>
          ) : null}
        </div>
        <p className="pod-legend">
          Размер ↗ — серия в CRM · ▾ — несколько экземпляров · город — фильтр ·
          БР — без размера
        </p>

        {data.serial ? (
          <div key={`self-${filterKey}`}>
            <div className="pod-sec">
              <h2>Эта модель</h2>
            </div>
            {self.length ? (
              self.map((m) => (
                <WideModel key={m.model_id} m={m} sub="модель серии" f={f} />
              ))
            ) : (
              <p className="pod-mute" style={{ padding: "0 var(--gx) 16px" }}>
                Свободных экземпляров этой модели сейчас нет — смотрите склейку
                и похожие ниже.
              </p>
            )}
            {glued.length ? (
              <>
                <div className="pod-sec">
                  <h2>Склейка — все цвета</h2>
                  <span className="pod-mute">
                    {data.serial.glue_total > glued.length
                      ? `${glued.length} из ${data.serial.glue_total} свободны`
                      : glued.length}
                  </span>
                </div>
                {gluedShown.length ? (
                  <div className="pod-grid">
                    {gluedShown.map((m) => (
                      <Tile
                        key={m.model_id}
                        m={m}
                        f={f}
                        sub={m.link === "group" ? "склейка" : "одинаковая в CRM"}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "0 var(--gx)" }}>
                    <Nothing f={f} />
                  </div>
                )}
              </>
            ) : null}
          </div>
        ) : null}

        <div className="pod-sec">
          <h2>{data.serial ? "Похожие по фото" : "Похожие"}</h2>
          <span className="pod-mute">{similar.length}</span>
        </div>
        {similar.length === 0 ? (
          <div style={{ padding: "0 var(--gx)" }}>
            <Nothing f={f} />
          </div>
        ) : (
          <>
            <div className="pod-grid" key={`grid-${filterKey}`}>
              {similar.slice(0, shown).map((m) => (
                <Tile key={m.model_id} m={m} f={f} />
              ))}
            </div>
            <div className="pod-pager">
              <span className="pod-mute">
                Показано {Math.min(shown, similar.length)} из {similar.length}
              </span>
              {shown < similar.length ? (
                <button
                  type="button"
                  className="pod-more"
                  onClick={() => setShown((n) => n + PAGE)}
                >
                  Показать ещё {Math.min(PAGE, similar.length - shown)}
                </button>
              ) : null}
              <button type="button" className="pod-link" onClick={toFilters}>
                К фильтрам ↑
              </button>
            </div>
          </>
        )}
        <footer className="pod-foot">
          Наличие — на момент открытия страницы. Бронь и готовность проверяйте в
          CRM.
        </footer>
      </main>
      {sheet ? (
        <AllFilters
          groups={groups}
          props={props}
          count={total}
          loading={filtersQ.isLoading}
          onToggle={toggle}
          onReset={() => set({ props: "" })}
          onClose={() => setSheet(false)}
        />
      ) : null}
    </Shell>
  );
}
